import type { CalendarEventDto } from '@chat-emp/shared';

import { Avatar } from '../components/Avatar.js';
import { Icon } from '../components/Icon.js';
import {
  formatDuration,
  formatEventDate,
  formatMonthShort,
  formatTimeRange,
} from '../util/format.js';
import { durationMinutes } from './event-rules.js';
import { guestCount, replyTally, responseLabel } from './invitees.js';
import { readVenue } from './location.js';
import { Soon } from './Soon.js';
import { statusLabel } from './status.js';
import styles from './EventView.module.css';

/**
 * An event as something to read — everything but its name, which the panel
 * showing this keeps in its own header.
 *
 * Built around the two questions anyone opens an event to answer — when is it,
 * and who is it with — rather than as a list of every field the record happens
 * to have. The time gets a chip and a line of its own; the venue gets one quiet
 * line at the bottom.
 *
 * No icon gutter. Grey glyphs down the left edge label values that already say
 * what they are ("14:00 – 14:30" has never needed a clock beside it), and they
 * cost a 24px indent on every row. Quiet section labels do the same work and can
 * be skipped by the eye, which is the point.
 *
 * The Who section is a list, however long. This is the one surface with room for
 * every name on a Chat Day, and the card that sent the reader here could only
 * say how many — so it prints them rather than a face and a "+23", which would
 * just be the card's answer again with more ceremony.
 */
export function EventView({ event }: { event: CalendarEventDto }) {
  const venue = readVenue(event.location);
  const status = statusLabel(event.status);
  // A list of one is a pre-invitee appointment or an ordinary interview; empty
  // is a row whose people all failed to resolve. See the DTO.
  const guests = event.invitees;
  const tally = replyTally(guests);

  return (
    <>
      <div className={styles.when}>
        {/* The date as a torn-off calendar leaf. One decorative element in the
            whole panel, spent on the fact everything else here depends on. */}
        <span className={styles.chip}>
          <span className={styles.chipMonth}>{formatMonthShort(event.startsAt)}</span>
          <span className={styles.chipDay}>{new Date(event.startsAt).getDate()}</span>
        </span>

        <span className={styles.whenText}>
          <span className={styles.whenDate}>{formatEventDate(event.startsAt)}</span>
          <span className={styles.whenTime}>
            {formatTimeRange(event.startsAt, event.endsAt)}
            <span className={styles.whenLength}>{formatDuration(durationMinutes(event))}</span>
          </span>
          <span className={styles.whenSoon}>
            <Soon startsAt={event.startsAt} endsAt={event.endsAt} />
          </span>
        </span>
      </div>

      {/* Nobody yet is an answer, not a gap: a Chat Day Jobie booked starts with
          no jobseeker on it, and they appear here as they join. */}
      {guests.length === 0 && (
        <section className={styles.section}>
          <p className={styles.label}>Who</p>
          <p className={styles.groupStatus}>Jobseekers show here once they join.</p>
        </section>
      )}
      {guests.length > 0 && (
        <section className={styles.section}>
          {/* The count replaces the label once there is one, because "Who" over a
              list of twenty-four is a heading that has stopped saying anything the
              list below it does not. */}
          <p className={styles.label}>{guests.length > 1 ? guestCount(guests) : 'Who'}</p>
          {/* Above the list, and it is the tally rather than the appointment's
              status: with several people the useful sentence is how many have
              answered, and each name carries its own answer below. The
              appointment's own state still shows on a one-to-one, beside the one
              person it can only be about. */}
          {guests.length > 1 && tally && <p className={styles.groupStatus}>{tally}</p>}
          <ul className={styles.people}>
            {guests.map((guest) => (
              // Keyed on the member id: the invitee row id is absent on a legacy
              // appointment, and a key that is sometimes undefined is not one.
              <li key={guest.userId} className={styles.person}>
                <Avatar name={guest.name} size={28} />
                <span className={styles.personName}>{guest.name}</span>
                {guests.length > 1 ? (
                  <span className={styles.status}>{responseLabel(guest.response)}</span>
                ) : (
                  /* Only where it says something — see statusLabel. */
                  status && <span className={styles.status}>{status}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className={styles.section}>
        <p className={styles.label}>Where</p>
        {/* The pill names the kind, read off the words beside it, and the words
            are what the employer typed. A link is a link here: this panel is
            open at five to two because somebody is about to join the thing. */}
        <p className={styles.where}>
          <span className={styles.typePill}>
            <Icon name={venue.icon} size={13} />
          </span>
          {venue.href ? (
            <a
              className={styles.whereLink}
              href={venue.href}
              target="_blank"
              rel="noreferrer noopener"
            >
              {venue.label}
            </a>
          ) : (
            <span className={styles.whereNote}>{venue.label}</span>
          )}
        </p>
      </section>
    </>
  );
}
